import { ApiAddress } from 'api/types/ApiAddress';
import { ApiAdmin } from 'api/types/ApiAdmin';
import { ApiAdminInvite } from 'api/types/ApiAdminInvite';
import { ApiAdminRequest } from 'api/types/ApiAdminRequest';
import { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { ApiListingMini } from 'api/types/ApiListingMini';
import { ApiPreferredListingType } from 'api/types/ApiPreferredListingType';
import { ApiSlideshowData } from 'api/types/ApiSlideshowData';
import { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { ApiOrgId } from 'api/types/ApiTypedId';
import { ApiOrgSize } from 'modules/listing/api/org/types/ApiOrgSize';
import { ApiOrgTimeActive } from 'modules/listing/api/org/types/ApiOrgTimeActive';
import { ApiOrgType } from 'modules/listing/api/org/types/ApiOrgType';

import { OrgDashboardApiInvoiceMini } from './OrgDashboardApiInvoiceMini';

export type YesNoDontKnow = 'YES' | 'NO' | 'DONT-KNOW';

export type OrgDashboardApiOrg = {
  id: ApiOrgId;
  created: ApiISODateTimeString;
  name: string;
  type: 'ORG';
  orgType: ApiOrgType;
  description: string | null;
  descriptionTruncated: string | null;
  mission: string | null;
  missionTruncated: string | null;
  links: Array<string>;
  email: string;
  phone: string;
  address: ApiAddress;
  locationType: 'ONSITE' | 'REMOTE' | 'HYBRID';
  url: Record<UserLocale, string>;
  areasOfFocus: ApiAreaOfFocus[];
  areasOfFocusKeys: ApiAreaOfFocus[];
  jobs: Array<ApiListingMini>;
  internships: Array<ApiListingMini>;
  volops: Array<ApiListingMini>;
  events: Array<ApiListingMini>;
  logo: ApiFilestackImageData | null;
  banner: ApiFilestackImageData | null;
  slideshowItems: Array<ApiSlideshowData>;
  hiring?: boolean;
  seekingVolunteers?: boolean;
  hasUpcomingEvents?: boolean;
  is501c3: boolean;
  isPublished: boolean;
  isApproved: boolean;
  isAllowedToPost: boolean;
  isFrozen: boolean;
  canPayByCheck: boolean;
  canPostVolops: boolean;
  canPostEvents: boolean;
  canBuyCredits: boolean;
  canDeleteOrg: boolean;
  canAddAdmins: boolean;
  canAddOwners: boolean;
  hasInvoices: boolean;
  hasApi: boolean;
  hasAts: boolean; // @TODO: it's always true, remove
  canPostAnonymously: boolean;
  postingRequiresApproval: boolean;
  locale: UserLocale;
  governmentId: string | null;
  linkedinId: number | null;
  vatNumber: string | null;
  inCountryThatRequiresVat: boolean;
  featureFairRegistrations: boolean;
  admins: Array<ApiAdmin>;
  adminRequests: Array<ApiAdminRequest>;
  invites: Array<ApiAdminInvite>;
  invoices: OrgDashboardApiInvoiceMini[];
  frozenListings: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  itemsInCart: number;
  credits: {
    JOB: number;
    INTERNSHIP: number;
  };
  promoCredits: {
    JOB: number;
    INTERNSHIP: number;
  };
  searchApiKey: string;
  greenhouse: {
    boardToken: string;
    sourceToken: string;
    autoPublish: boolean;
    byUser: {
      id: string;
      name: string;
      email: string;
    } | null;
  } | null;
  jazzhr: {
    customerId: string;
    autoPublish: boolean;
    byUser: {
      id: string;
      name: string;
      email: string;
    } | null;
  } | null;
  prices: {
    listings: {
      JOB: number;
      INTERNSHIP: number;
    };
    usListings: {
      JOB: number;
      INTERNSHIP: number;
    };
    CREDIT_PACKS: Record<
      string,
      {
        type: string;
        price: number;
        quantity: number;
      }
    >;
  };
  recommendationsEnabled: boolean;
  surveyBannerEnabled: boolean;
  preferredListingTypes?: ApiPreferredListingType[];
  relatedJobAreas?: boolean | null;
  relatedJobCount?: number | null;
  clients?: string | null;
  isBCorp?: YesNoDontKnow;
  timeActive?: ApiOrgTimeActive | null;
  services?: string | null;
  orgSize?: ApiOrgSize | null;
  orgDataHash: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOrgDashboardApiOrg = (obj: any): obj is OrgDashboardApiOrg =>
  // Include any property exclusive to this type
  'orgDataHash' in obj;
